import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export type LeagueFormatType = 'standard' | 'superflex' | '2qb' | 'bestball' | 'dynasty';

interface LeagueFormatSelectorProps {
  selectedFormat: LeagueFormatType;
  onFormatChange: (format: LeagueFormatType) => void;
  variant?: 'dropdown' | 'pills' | 'toggle';
  className?: string;
}

const formatDetails = {
  standard: {
    label: 'Standard',
    shortLabel: 'STD',
    description: '1 QB, traditional scoring'
  },
  superflex: {
    label: 'Superflex',
    shortLabel: 'SF',
    description: 'QB/RB/WR/TE flex spot'
  },
  '2qb': {
    label: '2QB',
    shortLabel: '2QB',
    description: '2 QB requirement'
  },
  bestball: {
    label: 'Best Ball',
    shortLabel: 'BB',
    description: 'No waivers, best lineup auto-set'
  },
  dynasty: {
    label: 'Dynasty',
    shortLabel: 'DYN',
    description: 'Keep players year-to-year'
  }
};

export const LeagueFormatSelector: React.FC<LeagueFormatSelectorProps> = ({
  selectedFormat,
  onFormatChange,
  variant = 'pills',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const formats = Object.keys(formatDetails) as LeagueFormatType[];

  const handleFormatChange = (format: LeagueFormatType) => {
    onFormatChange(format);
  };

  // Pills variant - for desktop/tablet
  if (variant === 'pills') {
    return (
      <div className={`flex flex-wrap gap-2 justify-end ${className}`}>
        {formats.map(format => {
          const details = formatDetails[format];
          const isSelected = selectedFormat === format;
          return (
            <button
              key={format}
              onClick={() => handleFormatChange(format)}
              className={`
                px-3 py-2 rounded-full border text-sm whitespace-nowrap transition-colors
                ${isSelected 
                  ? 'bg-fiesta-yellow text-ink border-fiesta-yellow' 
                  : 'bg-white/5 text-zinc-200 border-white/10 hover:border-white/20'
                }
              `}
              title={details.description}
            >
              <span>{details.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Toggle variant - for compact spaces
  if (variant === 'toggle') {
    const currentIndex = formats.indexOf(selectedFormat);
    const nextFormat = formats[(currentIndex + 1) % formats.length];
    const details = formatDetails[selectedFormat];
    
    return (
      <button
        onClick={() => handleFormatChange(nextFormat)}
        className={`
          px-4 py-2 rounded-lg border transition-colors
          bg-neutral-800 text-neutral-200 border-neutral-700 
          hover:bg-neutral-700 hover:border-neutral-600
          font-medium ${className}
        `}
        title="Click to change league format"
      >
        <span>{details.label}</span>
        <svg className="w-4 h-4 ml-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4" />
        </svg>
      </button>
    );
  }

  // Dropdown variant (default for mobile, optional for desktop)
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          w-full px-4 py-3 rounded-lg flex items-center justify-between
          bg-neutral-800 text-neutral-200 border border-neutral-700
          hover:bg-neutral-700 hover:border-neutral-600 transition-colors
        "
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-400 tracking-wide">Format</span>
          <span className="font-medium">{formatDetails[selectedFormat].label}</span>
        </div>
        <ChevronDownIcon 
          className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="
          absolute top-full left-0 right-0 mt-2 py-2 rounded-lg
          bg-neutral-800 border border-neutral-700 shadow-xl z-50
        ">
          {formats.map(format => {
            const details = formatDetails[format];
            const isSelected = selectedFormat === format;
            return (
              <button
                key={format}
                onClick={() => {
                  handleFormatChange(format);
                  setIsOpen(false);
                }}
                className={`
                  w-full px-4 py-3 flex items-center gap-3 transition-colors
                  ${isSelected 
                    ? 'bg-purple-600 text-white' 
                    : 'text-neutral-300 hover:bg-neutral-700 hover:text-white'
                  }
                `}
              >
                <div className="flex-1 text-left">
                  <div className="font-medium">{details.label}</div>
                  <div className="text-xs opacity-75">{details.description}</div>
                </div>
                {isSelected && (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Hook for persisting format selection
export const useLeagueFormat = () => {
  const [format, setFormat] = useState<LeagueFormatType>(() => {
    const saved = localStorage.getItem('selectedLeagueFormat');
    return (saved as LeagueFormatType) || 'standard';
  });

  const updateFormat = (newFormat: LeagueFormatType) => {
    setFormat(newFormat);
    localStorage.setItem('selectedLeagueFormat', newFormat);
  };

  return { format, updateFormat };
};
